import React, { useEffect, useRef, useState } from 'react';
import InstructionsModal from './InstructionsModal';
import { FaSignInAlt } from 'react-icons/fa';
import { ImAddressBook, ImProfile } from "react-icons/im";
import { AiOutlineMenu } from 'react-icons/ai';
import {Link, useNavigate} from 'react-router-dom';
import { toast } from 'react-toastify';
import lauri_logo from '../logo192.png';
import { useAuth } from '../context/AuthProvider';
import { useSpotContext } from '../context/SpotProvider';
import { PiArrowFatLineRight } from "react-icons/pi";
import { ACTIVITY_ID } from '../constants/activity';
import { axiosPrivate } from '../api/axios';

const SPOT_STATUS_LOCKED = process.env.REACT_APP_SPOT_STATUS_LOCKED;
const SPOT_STATUS_FREE = process.env.REACT_APP_SPOT_STATUS_FREE;

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [sellerMenuOpen, setSellerMenuOpen] = useState(false);
  const [publicMenuOpen, setPublicMenuOpen] = useState(false);
  const [managementMenuOpen, setManagementMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const profileMenuRef = useRef(null);
  const { isAuthenticated, setAuth, auth, isAdmin } = useAuth();
  const {spots, unlockSpot} = useSpotContext();
  const navigate = useNavigate();

  const userIsAdmin = Boolean(isAdmin?.() ?? auth?.isAdmin ?? auth?.user?.isAdmin);
  const profileDisplayName = auth?.user?.name ?? auth?.user?.fullName ?? auth?.user?.email ?? 'Käyttäjä';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = () => {
    setNav(!nav);
    setProfileMenuOpen(false);
  };

  const toggleSellerMenu = () => {
    setSellerMenuOpen((prev) => {
      const next = !prev;
      if (next) {
        setPublicMenuOpen(false);
        setManagementMenuOpen(false);
      }
      return next;
    });
  };

  const togglePublicMenu = () => {
    setPublicMenuOpen((prev) => {
      const next = !prev;
      if (next) {
        setSellerMenuOpen(false);
        setManagementMenuOpen(false);
      }
      return next;
    });
  };

  const toggleManagementMenu = () => {
    setManagementMenuOpen((prev) => {
      const next = !prev;
      if (next) {
        setSellerMenuOpen(false);
        setPublicMenuOpen(false);
      }
      return next;
    });
  };

  const handleLogout = () => {
        
      console.log("Navbar - handleLogout");
      spots.filter(spot => spot.userID === auth.user?.userID && spot.status === SPOT_STATUS_LOCKED).forEach(spot => {
        unlockSpot(spot,SPOT_STATUS_FREE);
      })
      
      setProfileMenuOpen(false);
      setAuth({});
      toast.info("Uloskirjautuminen onnistui");      
      navigate("/");
  };

  const openProfilePage = () => {
    setProfileMenuOpen(false);
    navigate('/profiili');
  };

  const openLoginPage = () => {
    setNav(false);
    setProfileMenuOpen(false);
    navigate('/login');
  };

  const openReservationsPdf = async () => {
    const pdfWindow = window.open('', '_blank');

    try {
      const response = await axiosPrivate.get('/reservationsCombined.php', {
        params: { activityID: ACTIVITY_ID },
        responseType: 'blob',
        headers: {
          'x-auth-token': auth?.accessToken,
        },
      });
      const pdfUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      if (pdfWindow) {
        pdfWindow.location.href = pdfUrl;
      } else {
        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.target = '_blank';
        downloadLink.rel = 'noreferrer';
        downloadLink.click();
      }

      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
      setNav(false);
    } catch (error) {
      if (pdfWindow) {
        pdfWindow.close();
      }
      toast.error('Varausraportin luonti epäonnistui.');
    }
  };

  const sellerMenuItems = [
    {
      key: 'spots-map',
      to: '/myyntipaikat-kartta',
      label: 'Myyntipaikat',
    },
    {
      key: 'spots-prices',
      to: '/myyntipaikat-hinnat',
      label: 'Hinnasto',
    },
    {
      key: 'contacts',
      to: '/yhteystiedot',
      label: 'Yhteystiedot',
      icon: <ImAddressBook className='inline mr-1' />,
    },
    {
      key: 'safety-instructions',
      href: 'https://www.lcmynamaki.fi/wp-content/uploads/LaurinMarkkinat/Turvallisuusohje.pdf',
      label: 'Turvallisuusohje',
    },
    {
      key: 'privacy-notice',
      href: 'https://www.lcmynamaki.fi/wp-content/uploads/LaurinMarkkinat/Laurin-markkinat-tietosuojaseloste_v10032025.pdf',
      label: 'Tietoturvaseloste',
    },
  ].filter(Boolean);

  const publicMenuItems = [
    {
      key: 'seller-list',
      to: '/myyjalista',
      label: 'Myyjälista',
    },
    {
      key: 'seller-map',
      to: '/myyntipaikat-kartta',
      label: 'Myyjät kartalla',
      state: { hideSpotInfo: true, publicSellerMap: true },
    },
    {
      key: 'cafe',
      to: '/myyntipaikat-kartta',
      label: 'Kahvila',
      state: { hideSpotInfo: true, publicSellerMap: true, openCafeModal: true },
    },
    {
      key: 'program',
      to: '/myyntipaikat-kartta',
      label: 'Ohjelma',
      state: { hideSpotInfo: true, publicSellerMap: true, openProgramModal: true },
    },
  ];

  const managementMenuItems = [
    userIsAdmin ? {
      key: 'manage-reservations-and-invoices',
      to: '/hallinta/varaukset-ja-laskut',
      label: 'Varaukset ja Laskut',
    } : null,
    userIsAdmin ? {
      key: 'manage-reservations-pdf',
      label: 'Varaukset (pdf)',
      onClick: openReservationsPdf,
    } : null,
    userIsAdmin ? {
      key: 'manage-audit-logs',
      to: '/hallinta/audit-lokit',
      label: 'Audit lokit',
    } : null,
    userIsAdmin ? {
      key: 'manage-electric-spots',
      to: '/myyntipaikat-kartta',
      label: 'Sähköpaikat',
      state: { hideSpotInfo: true, electricityManagementMap: true },
    } : null,
    !userIsAdmin ? {
      key: 'admin-login',
      to: '/hallinta/kirjaudu',
      label: 'Kirjaudu ylläpitäjänä',
    } : null,
  ].filter(Boolean);

  const showManagementMenu = !isAuthenticated() || (userIsAdmin && managementMenuItems.length > 0);

  return (

        /*
        <div className='flex justify-between items-center h-16 max-w-[1240px] mx-auto px-4 text-white'>
          <div className="flex"> 
                <img src={lauri_logo} alt="lauri-logo" className="mt-4 h-8 w-12 mr-6 pr-2"/><br/>                    
                <h1 className='w-full text-3xl font-bold text-white'>Laurin markkinat</h1>
            </div>
        */ 
     
    <div className='fixed top-0 left-0 z-40 w-full'>  
    <div className="flex h-14 items-center justify-between border-b border-slate-200/60 bg-white/92 px-3 text-slate-900 shadow-[0_10px_30px_rgba(15,23,42,0.08)] backdrop-blur md:px-5">
        <div className="flex items-center gap-2.5">
          <a href="/" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#163b73_0%,#1e63b5_70%,#f5b940_100%)] shadow-md">
            <img src={lauri_logo} alt="lauri-logo" className="h-6 w-auto" />
          </a>
          <div className="leading-tight">
            <div className="text-[12px] font-bold uppercase tracking-[0.24em] text-blue-700/80 md:text-[16px]">Laurin markkinat</div>
            <h1 className="text-[10px] font-black text-slate-900 md:text-[12px]">Myyntipaikat - info ja varaus</h1>
          </div>
        </div>
      <div className='flex items-center gap-2'>
        <button
          className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 md:text-sm"
          onClick={() => setShowInstructions(true)}
        >
          Ohje
        </button>

        {isAuthenticated() ? (
          <div className="relative" ref={profileMenuRef}>
            <button
              type="button"
              onClick={() => setProfileMenuOpen((prev) => !prev)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
              aria-label="Avaa profiilivalikko"
            >
              <ImProfile size={18} />
            </button>

            {profileMenuOpen ? (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] z-50 w-56 rounded-2xl border border-slate-200 bg-white p-2 text-slate-800 shadow-xl">
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                  <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Kirjautunut</div>
                  <div className="mt-1 break-words text-sm font-semibold text-slate-900">{profileDisplayName}</div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="mt-2 text-left text-sm font-medium text-red-600 transition hover:text-red-700"
                  >
                    Kirjaudu ulos
                  </button>
                </div>

                {userIsAdmin ? (
                  <div className="mt-2 flex w-full items-center rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-500">
                    <ImProfile className="mr-2" /> Ylläpitäjä
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={openProfilePage}
                    className="mt-2 flex w-full items-center rounded-xl px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-800"
                  >
                    <ImProfile className="mr-2" /> Profiilini
                  </button>
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={openLoginPage}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
            aria-label="Kirjaudu / Rekisteröidy"
            title="Kirjaudu / Rekisteröidy"
          >
            <FaSignInAlt size={16} />
          </button>
        )}

        <button
          type="button"
          onClick={handleNav}
          className='inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800'
          aria-label="Avaa valikko"
        >
          <AiOutlineMenu size={18}/>
        </button>
      </div>
    </div>
    {nav ? <button type="button" className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[1px]" onClick={() => setNav(false)} aria-label="Sulje valikko" /> : null}
    <ul className={nav ? 
  'fixed right-0 top-0 z-50 h-screen w-72 overflow-y-auto overflow-x-hidden border-l border-slate-200 bg-[linear-gradient(180deg,#0f2f5c_0%,#174a8d_58%,#0c3d74_100%)] px-4 pb-6 pt-6 text-white shadow-[-12px_0_32px_rgba(15,23,42,0.22)] transition-transform duration-300' 
  : 'fixed right-0 top-0 z-50 h-screen w-72 translate-x-full overflow-y-auto overflow-x-hidden border-l border-slate-200 bg-[linear-gradient(180deg,#0f2f5c_0%,#174a8d_58%,#0c3d74_100%)] px-4 pb-6 pt-6 text-white shadow-[-12px_0_32px_rgba(15,23,42,0.22)] transition-transform duration-300'}
>
  <button 
    type="button"
    onClick={() => setNav(false)} 
    className="ml-auto flex flex-row items-center text-sm font-semibold text-white/80 transition hover:text-white"
  >
    Sulje <PiArrowFatLineRight className="ml-2 mt-0.5" />
  </button>

  <div className='mt-6 rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm'>
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 shadow-inner">
        <img src={lauri_logo} alt="lauri-logo" className="h-8" />
      </div>
      <div>
        <div className='text-[11px] font-bold uppercase tracking-[0.28em] text-blue-100/80'>Navigointi</div>
        <h1 className='text-xl font-black text-white'>Laurin markkinat</h1>
      </div>
    </div>
  </div>

  <div className='mt-6 rounded-2xl border border-white/12 bg-slate-950/10 px-4 py-4'>
    <button
      type='button'
      onClick={toggleSellerMenu}
      className='flex w-full items-center justify-between border-b border-white/15 pb-3 text-left text-xs font-bold uppercase tracking-[0.25em] text-blue-100'
    >
      <span>Myyjille</span>
      <span className='text-base'>{sellerMenuOpen ? '−' : '+'}</span>
    </button>
    {sellerMenuOpen ? <ul>
      {sellerMenuItems.map((item) => (
        <li key={item.key} className='border-b border-white/10 py-2.5'>
          {item.href ? (
            <a
              href={item.href}
              target="_blank"
              rel="noreferrer"
              onClick={handleNav}
              className='flex items-center rounded-xl px-3 py-2 text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
            >
              {item.icon ? item.icon : null}
              {item.label}
            </a>
          ) : (
            <Link
              to={item.to}
              onClick={handleNav}
              className='flex items-center rounded-xl px-3 py-2 text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
            >
              {item.icon ? item.icon : null}
              {item.label}
            </Link>
          )}
        </li>
      ))}

    </ul> : null}
  </div>

  <div className='mt-4 rounded-2xl border border-white/12 bg-slate-950/10 px-4 py-4'>
    <button
      type='button'
      onClick={togglePublicMenu}
      className='flex w-full items-center justify-between border-b border-white/15 pb-3 text-left text-xs font-bold uppercase tracking-[0.25em] text-blue-100'
    >
      <span>Yleisölle</span>
      <span className='text-base'>{publicMenuOpen ? '−' : '+'}</span>
    </button>
    {publicMenuOpen ? <ul>
      {publicMenuItems.map((item) => (
        <li key={item.key} className='border-b border-white/10 py-2.5'>
          <Link
            to={item.to}
            state={item.state}
            onClick={handleNav}
            className='flex items-center rounded-xl px-3 py-2 text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
          >
            {item.label}
          </Link>
        </li>
      ))}
    </ul> : null}
  </div>

  {showManagementMenu ? (
    <div className='mt-4 rounded-2xl border border-white/12 bg-slate-950/10 px-4 py-4'>
      <button
        type='button'
        onClick={toggleManagementMenu}
        className='flex w-full items-center justify-between border-b border-white/15 pb-3 text-left text-xs font-bold uppercase tracking-[0.25em] text-blue-100'
      >
        <span>Hallinta</span>
        <span className='text-base'>{managementMenuOpen ? '−' : '+'}</span>
      </button>
      {managementMenuOpen ? <ul>
        {managementMenuItems.map((item) => (
          <li key={item.key} className='border-b border-white/10 py-2.5'>
            {item.onClick ? (
              <button
                type="button"
                onClick={item.onClick}
                className='flex w-full items-center rounded-xl px-3 py-2 text-left text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
              >
                {item.label}
              </button>
            ) : item.href ? (
              <a
                href={item.href}
                target="_blank"
                rel="noreferrer"
                onClick={handleNav}
                className='flex items-center rounded-xl px-3 py-2 text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
              >
                {item.label}
              </a>
            ) : (
              <Link
                to={item.to}
                state={item.state}
                onClick={handleNav}
                className='flex items-center rounded-xl px-3 py-2 text-sm font-medium text-white/95 transition hover:bg-white/10 hover:text-white'
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul> : null}
    </div>
  ) : null}
</ul>
      <InstructionsModal open={showInstructions} onClose={() => setShowInstructions(false)} />
    </div>
  );
};

export default Navbar;